<script setup>
import { computed, ref, onMounted, watch } from 'vue'
import TextInput from '../forms/inputs/text-input.vue';
import SelectInput from '../forms/inputs/select-input.vue';

// eslint-disable-next-line
const props = defineProps({
    // Data
    data: {
        type: Array,
        required: true
    },
    columns: {
        type: Array,
        required: true
    },
    keyField: {
        type: String,
        default: 'id'
    },

    title: {
        type: String,
        default: ''
    },

    subtitle: {
        type: String,
        default: ''
    },
    // Expandable rows
    expandable: {
        type: Boolean,
        default: false
    },
    expandedSlotName: {
        type: String,
        default: 'expanded-row'
    },
    expandedRowClasses: {
        type: String,
        default: 'bg-echoes-background-gray'
    },
    
    // Search
    showSearch: {
        type: Boolean,
        default: true
    },
    searchQuery: {
        type: String,
        default: ''
    },
    searchPlaceholder: {
        type: String,
        default: 'Search...'
    },
    
    // Pagination
    showPagination: {
        type: Boolean,
        default: true
    },
    currentPage: {
        type: Number,
        default: 1
    },
    itemsPerPage: {
        type: Number,
        default: 5
    },
    totalItems: {
        type: Number,
        required: true
    },
    serverPagination: {
        type: Boolean,
        default: false
    },
    showItemsPerPage: {
        type: Boolean,
        default: true
    },
    itemsPerPageOptions: {
        type: Array,
        default: () => [
            { label: '5', value: 5 },
            { label: '10', value: 10 },
            { label: '25', value: 25 },
            { label: '50', value: 50 }
        ]
    },
    
    // Sorting
    sortBy: {
        type: String,
        default: ''
    },
    sortOrder: {
        type: String,
        default: 'asc'
    },
    
    // Styling
    rowClasses: {
        type: String,
        default: 'bg-echoes-background-black/80 hover:bg-echoes-background-black/50 transition-all duration-200 border-b border-echoes-border-light'
    },
    
    // Messages
    emptyMessage: {
        type: String,
        default: 'No data available'
    },
    isLoading: {
        type: Boolean,
        default: false
    },
    loadingText: {
        type: String,
        default: 'Loading...'
    },
    redirectableColumns: {
        type: Array,
        default: () => []
    },

    // Selection
    select: {
        type: Boolean,
        default: false
    },
    selectedItems: {
        type: Array,
        default: () => []
    },
    selectable: {
        type: Function,
        default: () => true
    },
    selectAllByDefault: {
        type: Boolean,
        default: false
    }
})

// eslint-disable-next-line
const emit = defineEmits([
    'update:searchQuery',
    'update:currentPage',
    'update:itemsPerPage',
    'sort',
    'row-expanded',
    'row-collapsed',
    'update:selectedItems'
])

// Expanded rows state
const expandedRows = ref([])
const itemsPerPageId = ref(`table-items-per-page-${Math.random().toString(36).slice(2, 10)}`)

// Computed properties
const totalPages = computed(() => Math.ceil(props.totalItems / props.itemsPerPage))

const paginationStart = computed(() => ((props.currentPage - 1) * props.itemsPerPage) + 1)
const paginationEnd = computed(() => Math.min(props.currentPage * props.itemsPerPage, props.totalItems))

const paginatedData = computed(() => {
    if (!props.showPagination) {
        return props.data
    }
    if (props.serverPagination) {
        return props.data
    }
    
    const start = (props.currentPage - 1) * props.itemsPerPage
    const end = start + props.itemsPerPage
    return props.data.slice(start, end)
})

const generateSimplePages = (total) => {
    const pages = []
    for (let i = 1; i <= total; i++) {
        pages.push(i)
    }
    return pages
}

const generatePagesForStart = (total) => {
    const pages = []
    for (let i = 1; i <= 5; i++) pages.push(i)
    pages.push('...')
    pages.push(total)
    return pages
}

const generatePagesForEnd = (total) => {
    const pages = []
    pages.push(1)
    pages.push('...')
    for (let i = total - 4; i <= total; i++) pages.push(i)
    return pages
}

const generatePagesForMiddle = (current, total) => {
    const pages = []
    pages.push(1)
    pages.push('...')
    for (let i = current - 1; i <= current + 1; i++) pages.push(i)
    pages.push('...')
    pages.push(total)
    return pages
}

const visiblePages = computed(() => {
    const total = totalPages.value
    const current = props.currentPage

    if (total <= 7) {
        return generateSimplePages(total)
    }

    if (current <= 4) {
        return generatePagesForStart(total)
    }

    if (current >= total - 3) {
        return generatePagesForEnd(total)
    }

    return generatePagesForMiddle(current, total)
})

// Selection helpers
const selectableItems = computed(() => {
    return paginatedData.value.filter(item => props.selectable(item))
})

const allSelectableItems = computed(() => {
    return props.data.filter(item => props.selectable(item))
})

const allSelected = computed(() => {
    return allSelectableItems.value.length > 0 && allSelectableItems.value.every(item =>
        props.selectedItems.some(selectedItem => selectedItem[props.keyField] === item[props.keyField])
    )
})

const someSelected = computed(() => {
    return allSelectableItems.value.some(item =>
        props.selectedItems.some(selectedItem => selectedItem[props.keyField] === item[props.keyField])
    )
})

const isItemSelected = (item) => {
    return props.selectedItems.some(selectedItem => selectedItem[props.keyField] === item[props.keyField])
}

// Helper functions
const getItemKey = (item, index) => {
    return item[props.keyField] || index
}

const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj)
}

const getSortIcon = (column) => {
    if (props.sortBy !== column) return 'uil-arrow-down'
    return props.sortOrder === 'asc' ? 'uil-arrow-up' : 'uil-arrow-down'
}

const formatValue = (value, column) => {
    if (column.formatter && typeof column.formatter === 'function') {
        return column.formatter(value)
    }
    
    if (column.truncate && typeof value === 'string') {
        return value.length > column.truncate ? value.substring(0, column.truncate) + '...' : value
    }
    
    return value
}

const formatAmount = (amount) => {
    return `$${amount}`;
}
const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}
const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
}
// Expandable rows functions
const isRowExpanded = (item) => {
    const itemKey = item[props.keyField]
    return expandedRows.value.includes(itemKey)
}

const toggleRowExpansion = (item) => {
    const itemKey = item[props.keyField]
    const index = expandedRows.value.indexOf(itemKey)

    if (index > -1) {
        expandedRows.value.splice(index, 1)
        emit('row-collapsed', item)
    } else {
        expandedRows.value.push(itemKey)
        emit('row-expanded', item)
    }
}

// Selection functions
const toggleSelectAll = () => {
    let newSelectedItems = [...props.selectedItems]

    if (allSelected.value) {
        // Deselect all selectable items from all data
        for (const item of allSelectableItems.value) {
            const itemKey = item[props.keyField]
            newSelectedItems = newSelectedItems.filter(selectedItem => selectedItem[props.keyField] !== itemKey)
        }
    } else {
        // Select all selectable items from all data that aren't already selected
        for (const item of allSelectableItems.value) {
            const itemKey = item[props.keyField]
            const alreadySelected = newSelectedItems.some(selectedItem => selectedItem[props.keyField] === itemKey)
            if (!alreadySelected) {
                newSelectedItems.push(item)
            }
        }
    }

    emit('update:selectedItems', newSelectedItems)
}

const toggleItemSelection = (item) => {
    if (!props.selectable(item)) return

    const itemKey = item[props.keyField]
    let newSelectedItems = [...props.selectedItems]
    const isCurrentlySelected = isItemSelected(item)

    if (isCurrentlySelected) {
        newSelectedItems = newSelectedItems.filter(selectedItem => selectedItem[props.keyField] !== itemKey)
    } else {
        newSelectedItems.push(item)
    }

    emit('update:selectedItems', newSelectedItems)
}

const autoSelectAllItems = () => {
    if (props.selectAllByDefault && props.select && props.data.length > 0) {
        const selectableItems = props.data.filter(item => props.selectable(item))
        const newSelectedItems = selectableItems.filter(item =>
            !props.selectedItems.some(selectedItem => selectedItem[props.keyField] === item[props.keyField])
        )

        if (newSelectedItems.length > 0) {
            emit('update:selectedItems', [...props.selectedItems, ...newSelectedItems])
        }
    }
}

watch(() => props.data, (newData) => {
    if (props.selectAllByDefault && newData.length > 0) {
        autoSelectAllItems()
    }
}, { immediate: false })

onMounted(() => {
    if (props.selectAllByDefault && props.data.length > 0) {
        autoSelectAllItems()
    }
})
</script>
<template>
    <div>
        <div class="bg-echoes-background-gray rounded-xl border border-echoes-border-light overflow-hidden">
            <!-- Search and Filters -->
            <div class="p-3" v-if="showSearch || showItemsPerPage || $slots.filters">
                <div class="flex flex-col sm:flex-row gap-3 items-center justify-between">

                    <div class="flex flex-col justify-center w-full py-1">
                        <slot name="title">
                            <h1 class="hidden md:block ml-1 text-xl font-bold bg-gradient-to-r from-[#FFFFFF] to-[#999999] bg-clip-text text-transparent w-fit">
                                {{ title }}
                            </h1>
                            <h2 class="hidden md:block ml-1 text-sm font-medium text-white/60 w-fit">
                                {{ subtitle }}
                            </h2>
                        </slot>
                    </div>

                
                    
                    <!-- Custom Filters Slot -->
                    <div v-if="$slots.filters" class="flex gap-4">
                        <slot name="filters"></slot>
                    </div>
                    
                    <!-- Items Per Page -->
             
                            <!-- Search Input -->
                    <div class="w-full sm:w-96" v-if="showSearch">
                        <TextInput 
                            :model-value="searchQuery" 
                            @update:model-value="$emit('update:searchQuery', $event)"
                            :placeholder="searchPlaceholder"
                            class="w-full"
                        >
                        <template #icon>
                    <i class="uil uil-search"></i>
                </template>
                </TextInput>
                    </div>
                    <div class="sm:w-24" v-if="showItemsPerPage">
                        <SelectInput
                            :id="itemsPerPageId"
                            :model-value="itemsPerPage"
                            @update:model-value="$emit('update:itemsPerPage', $event)"
                            :options="itemsPerPageOptions"
                            :hide-search="true"
                        />
                    </div>
                    
                </div>
            </div>
    
            <!-- Table -->
            <div>
                <table class="min-w-full">
                    <thead class="bg-echoes-border-light border-b border-echoes-border-light">
                        <tr>
                            <!-- Selection Column Header -->
                            <th
                                v-if="select"
                                scope="col"
                                class="px-4 py-3 text-xs font-medium text-white uppercase tracking-wider text-center w-12"
                            >
                                <input
                                    type="checkbox"
                                    :checked="allSelected"
                                    :indeterminate="someSelected && !allSelected"
                                    @change="toggleSelectAll"
                                    :class="[
                                        'w-5 h-5 rounded-md border border-echoes-border-gray bg-echoes-background-black transition-all duration-200 cursor-pointer text-white',
                                        'appearance-none relative',
                                        allSelected ? 'bg-echoes-primary border-echoes-primary' : '',
                                        'hover:border-echoes-primary hover:bg-echoes-background-black/70 text-white'
                                    ]"
                                />
                            </th>
                            <th
                                v-for="column in columns"
                                :key="column.key"
                                scope="col"
                                :class="[
                                    'px-4 py-3 text-xs font-medium text-white uppercase tracking-wider',
                                    column.wrap ? 'whitespace-normal' : 'whitespace-nowrap',
                                    column.align === 'center' ? 'text-center' :
                                    column.align === 'right' ? 'text-right' : 'text-left',
                                    column.sortable ? 'cursor-pointer hover:bg-echoes-background-black/20 transition-all duration-200' : ''
                                ]"
                                @click="column.sortable ? $emit('sort', column.key) : null"
                            >
                                <span :class="column.wrap ? 'flex items-center gap-1' : 'inline-flex items-center gap-1'">
                                    <span v-if="Array.isArray(column.label)" :class="column.wrap ? 'flex flex-col leading-tight' : ''">
                                        <span v-for="(line, index) in column.label" :key="index">{{ line }}</span>
                                    </span>
                                    <span v-else>{{ column.label }}</span>
                                    <i
                                        v-if="column.sortable"
                                        :class="[
                                            'uil text-base',
                                            getSortIcon(column.key),
                                            props.sortBy === column.key ? 'text-echoes-primary' : 'text-white'
                                        ]"
                                    ></i>
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-echoes-background-gray">
                        <template v-for="(item, index) in paginatedData" :key="item[keyField]">
                            <!-- Main Row -->
                            <tr :class="rowClasses">
                                <!-- Selection Column -->
                                <td
                                    v-if="select"
                                    class="px-4 py-3 text-sm text-center w-12"
                                >
                                    <input
                                        v-if="selectable(item)"
                                        type="checkbox"
                                        :checked="isItemSelected(item)"
                                        @change="toggleItemSelection(item)"
                                        :class="[
                                            'w-5 h-5 rounded-md border transition-all duration-200 cursor-pointer text-white',
                                            'appearance-none relative',
                                            isItemSelected(item)
                                                ? 'bg-echoes-primary border-echoes-primary hover:bg-echoes-primary/50'
                                                : 'bg-echoes-background-black/50 border-echoes-border-light',
                                            'hover:border-echoes-primary hover:bg-echoes-background-black/70'
                                        ]"
                                    />
                                    <div v-else class="w-4 h-4"></div>
                                </td>
                                <td
                                    v-for="column in columns"
                                    :key="column.key"
                                    class="px-4 py-3 text-sm"
                                    @click="redirectableColumns.includes(column.key) ? $emit('redirect', item, column.key) : null"
                                >
                                    <!-- Custom Column Slot -->
                                    <slot 
                                        :name="`column-${column.key}`" 
                                        :item="item" 
                                        :value="getNestedValue(item, column.key)"
                                        :index="(currentPage - 1) * itemsPerPage + index"
                                        :is-expanded="isRowExpanded(item)"
                                        :toggle-expand="() => toggleRowExpansion(item)"
                                    >
                                        <!-- Default Column Rendering -->
                                        <div v-if="column.type === 'text'" :class="[column.textClass || 'text-sm text-white']">
                                            <span v-if="redirectableColumns.includes(column.key)" class="cursor-pointer hover:text-echoes-primary transition-colors duration-200">
                                                {{ formatValue(getNestedValue(item, column.key), column) }}
                                            </span>
                                            <span v-else>
                                                {{ formatValue(getNestedValue(item, column.key), column) }}
                                            </span>
                                        </div>
                                        <div v-else-if="column.type === 'amount'" class="text-sm text-white">
                                            {{ formatAmount(getNestedValue(item, column.key)) }}
                                        </div>
                                        <div v-else-if="column.type === 'date'" class="flex flex-col justify-center">
                                            <div class="text-sm font-medium text-white">
                                                {{ formatDate(getNestedValue(item, column.key)) }}
                                            </div>
                                            <div class="text-sm text-white/80">
                                                at {{ formatTime(getNestedValue(item, column.key)) }}
                                            </div>
                                        </div>
                                        <div v-else class="text-sm text-white">
                                            {{ getNestedValue(item, column.key) }}
                                        </div>
                                    </slot>
                                </td>
                            </tr>
                            
                            <!-- Expandable Row -->
                            <tr
                                v-if="expandable && isRowExpanded(item)"
                                :class="expandedRowClasses"
                            >
                                <td :colspan="select ? columns.length + 1 : columns.length" class="px-0 py-0">
                                    <div class="px-6 py-4 bg-echoes-background-gray">
                                        <slot 
                                            :name="expandedSlotName" 
                                            :item="item" 
                                            :index="index"
                                            :close="() => toggleRowExpansion(item)"
                                        >
                                            <div class="text-white/80">
                                                Expanded content for {{ getItemKey(item, index) }}
                                            </div>
                                        </slot>
                                    </div>
                                </td>
                            </tr>
                        </template>
                        
                        <!-- Empty/Loading State -->
                        <tr v-if="paginatedData.length === 0">
                            <td :colspan="select ? columns.length + 1 : columns.length" class="px-6 py-12 text-center">
                                <div v-if="isLoading" class="flex flex-col items-center justify-center text-white/80">
                                    <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-echoes-primary"></div>
                                    <p class="text-sm mt-3">{{ loadingText }}</p>
                                </div>
                                <div v-else class="text-white/60">
                                    <slot name="empty">
                                        {{ emptyMessage }}
                                    </slot>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                
                <!-- Pagination -->
                <div 
                    v-if="showPagination && paginatedData.length !== 0" 
                    class="px-6 py-4"
                >
                    <div class="flex items-center justify-between">
                        <div class="flex items-center text-sm text-white/80">
                            <span>Showing</span>
                            <span class="font-medium mx-1">{{ paginationStart }}</span>
                            <span>to</span>
                            <span class="font-medium mx-1">{{ paginationEnd }}</span>
                            <span>of</span>
                            <span class="font-medium ml-1">{{ totalItems }}</span>
                        </div>
                        <div class="flex items-center space-x-2">
                            <button
                                @click="$emit('update:currentPage', Math.max(1, currentPage - 1))"
                                :disabled="currentPage === 1"
                                class="px-3 py-1.5 flex items-center justify-center rounded-lg text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <div class="flex items-center space-x-1">
                                <template v-for="(page, index) in visiblePages" :key="index">
                                    <template v-if="typeof page === 'number'">
                                        <button
                                            @click="$emit('update:currentPage', page)"
                                            :class="[
                                                'px-3 py-1.5 flex items-center justify-center rounded-lg text-sm font-medium',
                                                currentPage === page
                                                    ? 'bg-echoes-primary text-white'
                                                    : 'text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80'
                                            ]"
                                        >
                                            {{ page }}
                                        </button>
                                    </template>
                                    <template v-else>
                                        <span class="px-3 py-1.5 flex items-center justify-center text-sm text-white/60">...</span>
                                    </template>
                                </template>
                            </div>
                            <button
                                @click="$emit('update:currentPage', Math.min(totalPages, currentPage + 1))"
                                :disabled="currentPage === totalPages || totalPages === 0"
                                class="px-3 py-1.5 flex items-center justify-center rounded-lg text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Custom checkmark for checked state */
input[type="checkbox"]:checked::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 6px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: translate(-50%, -50%) rotate(45deg);
}
</style>
